// [S-061] Templates — read-only browser for L1 + tenant L2
// templates in `certified` / `re_certified` states. Direct port of
// docs/Wireframes/Templates.html with three deviations called out:
//
//   1. Filter row uses Radix Select primitives (S-061f) wrapped to
//      look like the wireframe's chip-styled "+ Talent tier" picker —
//      keeps the MVP keyboard-accessible with proper a11y semantics
//      and a branded dropdown rather than the OS-native menu.
//   2. The "Precedents" chip on the filter row is a navigation
//      shortcut to the full precedents search screen at /precedent
//      (S-061b). It lives here because attorneys browsing the
//      certified templates frequently want to cross-check against their
//      studio's prior deals — keeping the shortcut on this row saves a
//      sidebar trip.
//   3. Card grid is virtualisation-free: L1 is bounded (~2k templates)
//      and the page is read-mostly, so paginating server-side is a
//      cost we don't need to pay yet.
//
// Authoring + lifecycle management (drafts, flag-for-review, deprecate,
// "+ New L1/L2 template") moved off this page. The Template Management
// surface (S-062) owns those flows and is reachable from the sidebar
// under "Tenant admin" (tenant_admin) and "Platform" (platform_admin).
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useCertifiedTemplates,
  useTenantCertifiedTemplates,
  type CertifiedLibraryFilter,
  type CertifiedTemplateRow,
} from "../data/templatesQuery";
import {
  Alert,
  Button,
  PageHead,
  Pagination,
  Select,
  usePagination,
} from "../components";
import { useSession } from "../auth/AuthProvider";
import { TALENT_TIERS, talentTierLabel } from "../lib/talentTier";
import { useActorLabels } from "../data/identitiesQuery";
import type { RecordRef } from "../data/chatQuery";
import { RecordDrawerProvider, useRecordDrawer } from "./agents/RecordDrawer";

const DEAL_TYPES: ReadonlyArray<{
  value: CertifiedTemplateRow["dealType"];
  label: string;
}> = [
  { value: "feature_film", label: "Feature film" },
  { value: "tv_series", label: "TV series" },
  { value: "streaming", label: "Streaming" },
  { value: "documentary", label: "Documentary" },
  { value: "commercial", label: "Commercial" },
  { value: "short_form", label: "Short form" },
];

const GUILDS: ReadonlyArray<{
  value: CertifiedTemplateRow["guild"];
  label: string;
}> = [
  { value: "SAG", label: "SAG-AFTRA" },
  { value: "DGA", label: "DGA" },
  { value: "WGA", label: "WGA" },
  { value: "IATSE", label: "IATSE" },
  { value: "AFM", label: "AFM" },
  { value: "PGA", label: "PGA" },
];

const TIERS = TALENT_TIERS;

// [S-633 follow-up] "Deal memo" (not "Short form") — DEAL_TYPES above already
// uses "Short form" for an unrelated dealType value; reusing it here for
// documentForm would put two differently-meaning "Short form" chips on the
// same filter bar.
const DOCUMENT_FORMS: ReadonlyArray<{
  value: NonNullable<CertifiedTemplateRow["documentForm"]>;
  label: string;
}> = [
  { value: "deal_memo", label: "Deal memo" },
  { value: "long_form", label: "Long form" },
];

export function Templates(): JSX.Element {
  const [filter, setFilter] = useState<CertifiedLibraryFilter>({});
  const session = useSession();

  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";

  const l1 = useCertifiedTemplates(filter);
  const l2 = useTenantCertifiedTemplates(tenantId, filter);

  const isLoading =
    l1.isLoading || (tenantId.length > 0 && l2.isLoading);
  const isFetching =
    l1.isFetching || (tenantId.length > 0 && l2.isFetching);
  const isError = l1.isError || l2.isError;
  const error = l1.error ?? l2.error;

  const rows = useMemo<CertifiedTemplateRow[]>(() => {
    const a = l1.data ?? [];
    const b = l2.data ?? [];
    const seen = new Set<string>();
    const out: CertifiedTemplateRow[] = [];
    for (const t of [...b, ...a]) {
      if (seen.has(t.id)) continue;
      seen.add(t.id);
      out.push(t);
    }
    return out;
  }, [l1.data, l2.data]);
  const pager = usePagination(rows, 24);
  const ownerUids = useMemo(
    () => pager.pageItems.map((t) => t.submittedBy),
    [pager.pageItems]
  );
  const ownerLabels = useActorLabels(ownerUids, tenantId);

  return (
    <RecordDrawerProvider>
      <PageHead
        eyebrow={
          <>
            <span className="src-dot cert" aria-hidden="true" />
            Templates
          </>
        }
        title={
          <>
            The book of <em>standard</em>.
          </>
        }
        sub={
          <>
            Approved by ContractStudio attorneys.{" "}
            {isLoading
              ? "Loading…"
              : `${rows.length.toLocaleString()} template${rows.length === 1 ? "" : "s"} match your filters.`}
          </>
        }
      />
      <FilterBar filter={filter} onChange={setFilter} count={rows.length} />

      {(isLoading || (isFetching && rows.length === 0)) && (
        <div className="tpl__state" role="status" aria-live="polite">
          Loading certified templates…
        </div>
      )}

      {isError && (
        <Alert label="Couldn't load templates" tone="error">
          {error?.message ?? "Unknown error"}
        </Alert>
      )}

      {!isLoading && !isFetching && !isError && rows.length === 0 && (
        <div className="tpl__state">
          <div className="tpl__state__title">No certified templates match</div>
          Loosen the filter row above, or clear it to see all templates.
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <div className="tpl__grid" role="list">
            {pager.pageItems.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                ownerLabel={ownerLabels?.get(t.submittedBy)}
              />
            ))}
          </div>
          <Pagination
            page={pager.page}
            pageCount={pager.pageCount}
            onPageChange={pager.setPage}
            total={pager.total}
            rangeStart={pager.rangeStart}
            rangeEnd={pager.rangeEnd}
            itemLabel="template"
          />
        </>
      )}
    </RecordDrawerProvider>
  );
}

type FilterBarProps = {
  filter: CertifiedLibraryFilter;
  onChange: (next: CertifiedLibraryFilter) => void;
  count: number;
};

function FilterBar({ filter, onChange, count }: FilterBarProps): JSX.Element {
  return (
    <div className="tpl__filters">
      <span className="tpl__filterlbl">Filter</span>

      <FilterSelect
        label="Deal type"
        value={filter.dealType}
        options={DEAL_TYPES}
        onChange={(value) => onChange({ ...filter, dealType: value })}
      />

      <FilterSelect
        label="Guild"
        value={filter.guild}
        options={GUILDS}
        onChange={(value) => onChange({ ...filter, guild: value })}
      />

      <FilterSelect
        label="Talent tier"
        value={filter.tier}
        options={TIERS}
        onChange={(value) => onChange({ ...filter, tier: value })}
      />

      <FilterSelect
        label="Document form"
        value={filter.documentForm}
        options={DOCUMENT_FORMS}
        onChange={(value) => onChange({ ...filter, documentForm: value })}
      />

      {/* S-061b: the Precedents toggle now deep-links to the
          full precedents search screen. (The toggle is a navigation
          shortcut, not an in-place filter — searching for precedents
          surfaces a different layer with its own conflict UI and tier
          gate, so it deserves its own page.) */}
      <Link
        to="/precedent"
        className="tpl__filter"
        title="Search your studio's prior deals"
      >
        Precedents →
      </Link>

      <span className="tpl__count">
        {count.toLocaleString()} template{count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

function FilterSelect<V extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: V | undefined;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChange: (value: V | undefined) => void;
}): JSX.Element {
  const isOn = value !== undefined;
  // Radix Select wrapped to look like a chip when closed, with a
  // branded popdown when open (replaces the previous native <select>
  // styled with appearance: none). Existing `tpl__filter` chip CSS
  // still applies to the trigger; the popdown comes from `ds-select__*`.
  return (
    <span className={isOn ? "tpl__filter is-on" : "tpl__filter"}>
      <Select
        label={label}
        value={value}
        options={options}
        onValueChange={onChange}
        triggerClassName="tpl__filter__select"
      />
      {isOn && (
        <Button
          variant="bare"
          className="tpl__filter__clear"
          aria-label={`Clear ${label}`}
          onClick={() => onChange(undefined)}
        >
          ×
        </Button>
      )}
    </span>
  );
}

// [S-413 follow-up] Builds this template's RecordRef for the shared preview
// drawer — same widget Precedents/Drafts open, so browsing any of the three
// feels consistent. The id stays internal (never rendered raw); the card's
// visible label is `templateDisplayName`.
function templateRef(t: CertifiedTemplateRow): RecordRef {
  const id = t.id;
  const uri = `csai://template/${t.tenantId ?? "global"}/${id}`;
  return {
    type: "template",
    id,
    tenantId: t.tenantId,
    title: templateDisplayName(t),
    subtitle: `${prettyDealType(t.dealType)} · ${prettyGuild(t.guild)}`,
    uri,
  };
}

// Three-zone card mirroring the wireframe redesign:
//   HEAD — mono id + status pills (L1/L2 + Certified/Re-certified)
//   BODY — serif title with italic tier accent + filter chips
//   FOOT — raised meta strip: attorney avatar/name + effective date,
//          arrow circle that fades in on hover.
//
// [S-413 follow-up] Clicking opens the shared record drawer (same widget as
// Precedents/Drafts) rather than navigating away — "Open in new tab" inside
// the drawer is the full-page escape hatch. We don't fabricate fields the
// API doesn't return: version, anchored-CBA name, and "used in N deals"
// from the design are omitted rather than mocked.
function TemplateCard({
  template,
  ownerLabel,
}: {
  template: CertifiedTemplateRow;
  ownerLabel: string | undefined;
}): JSX.Element {
  const isL2 = template.layerSource === "L2";
  const displayName = templateDisplayName(template);
  const { openRecord } = useRecordDrawer();
  return (
    <Button
      variant="bare"
      onClick={() => openRecord(templateRef(template))}
      className={isL2 ? "tpl__card tpl__card--l2" : "tpl__card"}
      role="listitem"
      aria-label={`Open ${displayName}`}
      data-testid={`tpl-row-${template.id}`}
    >
      <div className="tpl__card__head">
        <div className="tpl__card__id">
          <span className="id">{displayName}</span>
        </div>
        <div className="tpl__card__pills">
          <span
            className={isL2 ? "tpl__pill tpl__pill--l2" : "tpl__pill tpl__pill--l1"}
            title={
              isL2
                ? "Studio template — curated by your organization"
                : "Templates — shared across all organizations"
            }
          >
            {template.layerSource}
          </span>
          <span className="tpl__pill tpl__pill--cert">
            <span className="check" aria-hidden="true" />
            {template.lifecycleState === "re_certified"
              ? "Re-certified"
              : "Certified"}
          </span>
        </div>
      </div>

      <div className="tpl__card__body">
        <div className="tpl__card__title">
          {prettyGuild(template.guild)} {prettyDealType(template.dealType)}{" "}
          <em>— {prettyTier(template.tier)}</em>
        </div>
        <div className="tpl__card__chips">
          <span className="chip--guild">{prettyGuild(template.guild)}</span>
          <span>{prettyDealType(template.dealType)}</span>
          <span>{prettyTier(template.tier)}</span>
          {/* [S-633 follow-up] Only shown when tagged — an untagged chip on
              every card (most seed/legacy rows) would be noise here; the
              detail page's hero pill is the honest-signal spot for that. */}
          {template.documentForm != null && (
            <span>{prettyDocumentForm(template.documentForm)}</span>
          )}
        </div>
      </div>

      <div className="tpl__card__foot">
        <div className="tpl__card__by">
          {ownerLabel !== undefined && (
            <span className="av" aria-hidden="true">
              {initials(ownerLabel)}
            </span>
          )}
          <div className="who">
            {ownerLabel ?? "—"}
            <em>Effective {formatDate(template.effectiveFrom)}</em>
          </div>
        </div>
        <span className="tpl__card__arr" aria-hidden="true">
          →
        </span>
      </div>
    </Button>
  );
}

function prettyGuild(g: CertifiedTemplateRow["guild"]): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyDealType(d: CertifiedTemplateRow["dealType"]): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function prettyTier(t: CertifiedTemplateRow["tier"]): string {
  return talentTierLabel(t);
}

function prettyDocumentForm(d: CertifiedTemplateRow["documentForm"]): string {
  return d === "deal_memo" ? "Deal memo" : "Long form";
}

// The name a browser recognizes: the uploaded title when set, else a
// readable deal/guild/tier label (legacy/seeded rows have no title). Never
// the opaque id.
function templateDisplayName(t: CertifiedTemplateRow): string {
  const title = t.title?.trim();
  if (title !== undefined && title.length > 0) return title;
  return `${prettyGuild(t.guild)} ${prettyDealType(t.dealType)} — ${prettyTier(t.tier)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (first === undefined || last === undefined) return "?";
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
}
